import classNames from 'classnames';
import errorIcon from '../../../../img/Icons_Feedback_Error.svg';
import infoIcon from '../../../../img/Icons_Feedback_Info.svg';
import successIcon from '../../../../img/Icons_Feedback_Success.svg';
import warningIcon from '../../../../img/Icons_Feedback_Warning.svg';
import translations from '../../translations';
import type {
    Notification as NotificationObject,
    NotificationType,
} from '../../types/notification';
import { useEffect, type ActionDispatch } from 'react';
import type { NotificationAction } from './NotificationProvider';
import ErrorTraceId from '../generic/ErrorTraceId';

const iconMap: Record<NotificationType, string> = {
    success: successIcon,
    warning: warningIcon,
    error: errorIcon,
    info: infoIcon,
};

type Props = {
    notification: NotificationObject;
    dispatchNotification: ActionDispatch<[action: NotificationAction]>;
};

const Notification = ({ notification, dispatchNotification }: Props) => {
    const { id, type, message, isPersistent, traceId } = notification;

    const isStringMessage = typeof message === 'string';

    // Special case, this message could be long so lets add some formatting to make it easier to read
    const tenantExportMessage =
        isStringMessage && message.includes('No flow snapshot could be found for Flow')
            ? message
                  .split('No flow')
                  .slice(1)
                  .map((x) => <p key={x}>{`No flow ${x}`}</p>)
            : null;

    const classes = classNames(`toast-notification ${type}`, {
        'toast-wide': isStringMessage && message.length > 250,
    });

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (!isPersistent) {
            timer = setTimeout(
                () =>
                    dispatchNotification({
                        type: 'removeNotification',
                        payload: {
                            id,
                        },
                    }),
                5000,
            );
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        };
    }, [id, isPersistent, dispatchNotification]);

    return (
        <div className={classes}>
            <img src={iconMap[type]} width={24} height={24} alt="" />
            <div className="toast-notification-message">
                {tenantExportMessage ?? message}
                <ErrorTraceId traceId={traceId} />
            </div>
            {isPersistent ? (
                <button
                    className="toast-notification-dismiss"
                    onClick={() => {
                        dispatchNotification({
                            type: 'removeNotification',
                            payload: {
                                id,
                            },
                        });
                    }}
                    aria-label={translations.NOTIFICATION_dismiss_button_label}
                    type="button"
                >
                    ×
                </button>
            ) : null}
        </div>
    );
};

export default Notification;
